import styled from "@emotion/styled";
import classNames from "classnames";
import React from "react";
import { CircularProgressbar } from "react-circular-progressbar";
import { CustomOnboardingRedirectionParams } from "../../activities/AppPage";
import { HRSection } from "../../messages";
import { STRINGS } from "../../strings";
import { TrainingCenterCaregiverBundle } from "../../system/AppState/AppStateTrainingCenter";
import { translate } from "../../translate";
import { doNothing } from "../../utils/doNothing";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import OnboardingChatIcon from "../OnboardingChatIcon";
import Scaffold from "../Scaffold/Scaffold";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import { TrainingCenterLanguagePreference } from "../TrainingCenter/TrainingCenterLanguageLookup";
import TrainingCenterBundleHeader from "../TrainingCenterBundle/components/TrainingCenterBundleHeader";
import OnboardingAccordion from "./OnboardingAccordion";
import SectionCard from "./components/SectionCard";
import { isSectionCompleted, isSectionVisible } from "./utils/onboardingUtils";

interface Props {
  sections: HRSection[];
  language: string;
  bundle: TrainingCenterCaregiverBundle | undefined;
  languagePreference: TrainingCenterLanguagePreference;
  showOnboardingMessage: boolean;
  isCriminalBackgroundCompliant: boolean;
  isInterviewCompliant: boolean | null;
  onClickChat: () => void;
  onClickGoBack: () => void;
  onClickLockedSection: () => void;
  onClickSection: (
    section: HRSection,
    sections: HRSection[],
    customDirectionParams: CustomOnboardingRedirectionParams | null
  ) => void;
  onClickWatchOrientation: () => void;
}

const OnboardingScreen = (props: Props) => {
  const chatAction = (
    <div className="OnboardingChatIcon">
      <OnboardingChatIcon badge={0} isPopperOpen={false} onClick={doNothing} />
    </div>
  );

  return (
    <OnboardingScaffold
      onClickGoBack={props.onClickGoBack}
      title={<OnboardingScreen.Title />}
      endActions={<OnboardingScreen.Actions sections={props.sections} />}
    >
      <OnboardingScaffoldBody>
        <OnboardingAccordion.Root type="single" collapsible>
          {props.sections.filter(isSectionVisible).map((section, i) => {
            return (
              <AppSlideFadeIn key={`sub-section-${section.data.id}`} delay={i * 100 + 250}>
                <OnboardingAccordion.Item value={"accordion-item-" + i}>
                  <SectionCard
                    section={section}
                    language={props.language}
                    isCriminalBackgroundCompliant={props.isCriminalBackgroundCompliant}
                    isInterviewCompliant={props.isInterviewCompliant}
                    onClickLocked={props.onClickLockedSection}
                    onClick={() => props.onClickSection(section, props.sections, null)}
                    onClickQuestion={(questionId) =>
                      props.onClickSection(section, props.sections, {
                        type: "Question",
                        questionId,
                        sectionId: section.data.id,
                      })
                    }
                  />
                </OnboardingAccordion.Item>
              </AppSlideFadeIn>
            );
          })}
        </OnboardingAccordion.Root>

        {props.bundle === undefined ? null : (
          <TrainingCenterBundleHeaderWrapper>
            <TrainingCenterBundleHeader
              bundle={props.bundle}
              languagePreference={props.languagePreference}
              onClickPlayBundle={doNothing}
              className={classNames("lever", "OnBoardingScreenBundleHeader")}
              showOnboardingMessage={props.showOnboardingMessage}
              onClick={props.onClickWatchOrientation}
            />
          </TrainingCenterBundleHeaderWrapper>
        )}
        <ChatIconWrapper onClick={props.onClickChat}>{chatAction}</ChatIconWrapper>
      </OnboardingScaffoldBody>
    </OnboardingScaffold>
  );
};

const TrainingCenterBundleHeaderWrapper = styled.div`
  .OnBoardingScreenBundleHeader {
    height: 250px;
  }

  .OnBoardingScreenBundleHeader > .bundle-thumbnail {
    border-radius: 32px;
  }

  .OnBoardingScreenBundleHeader > .bundle-thumbnail:after {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 100%);
  }
`;

const ChatIconWrapper = styled.div`
  .OnboardingChatIcon {
    position: absolute;
    right: 8px;
    bottom: 5%;
    z-index: 1;
  }
  .VisitChatIcon {
    --radius: 100px;
    margin: 0;
    box-shadow: 0px 0px 13px -3px grey;
    --expand: 20px;
    background-color: var(--primary-color);
  }

  .VisitChatIcon::before {
    animation: none;
  }

  .VisitChatIcon::after {
    animation: none;
  }
`;

const OnboardingScaffold = styled(Scaffold)``;

const OnboardingScaffoldBody = styled.div`
  margin: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

OnboardingScreen.Title = () => {
  return (
    <div>
      <ScaffoldTitle label={translate(STRINGS.ONBOARDING)} />
      <OnboardingScreen.SubTitle>
        {translate(STRINGS.FINISH_ONBOARDING_AND_GET_DOLLARS)}!
      </OnboardingScreen.SubTitle>
    </div>
  );
};

OnboardingScreen.Actions = (props: Pick<Props, "sections">) => {
  const sections = props.sections.filter(isSectionVisible);

  const completedSections = sections.filter(isSectionCompleted).length;

  return (
    <CircularProgressbar
      value={completedSections}
      maxValue={sections.length}
      strokeWidth={10}
      text={`${completedSections}/${sections.length}`}
      background={true}
      counterClockwise={true}
      styles={circularProgressbarStyles}
    />
  );
};

OnboardingScreen.SubTitle = styled.span`
  color: var(--gray-500);
  font-weight: 600;
  font-size: 14px;
`;

const circularProgressbarStyles = {
  root: {
    width: 45,
    height: 45,
  },
  background: {
    fill: "#FFF",
  },
  trail: {
    stroke: "var(--gray-100)",
  },
  path: {
    stroke: "var(--primary-color)",
  },
  text: {
    fill: "#272860",
    fontWeight: 800,
    fontSize: 22,
  },
};

export default OnboardingScreen;
